import React from "react";

interface Props {
  children: React.ReactNode;
}

interface State {
  hasError: boolean;
  error: Error | null;
}

/**
 * Catches render-time errors anywhere in the tree below it and shows a
 * fallback UI instead of letting the whole app unmount to a blank white page.
 * Also logs the real error so the underlying cause is visible.
 */
class ErrorBoundary extends React.Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, info: React.ErrorInfo) {
    // Always log, even in production, so the cause is recoverable.
    // eslint-disable-next-line no-console
    console.error("[ErrorBoundary] Caught a render error:", error, info);
  }

  handleReload = () => {
    window.location.reload();
  };

  render() {
    if (this.state.hasError) {
      return (
        <div
          style={{
            minHeight: "100vh",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            gap: "1rem",
            padding: "2rem",
            textAlign: "center",
            fontFamily:
              "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
            color: "#2c3e50",
            background: "#fffdf8",
          }}
        >
          <h1 style={{ fontSize: "1.5rem", fontWeight: 700 }}>
            Something went wrong
          </h1>
          <p style={{ color: "#666", maxWidth: "480px" }}>
            The page hit an unexpected error. Please reload — if it keeps
            happening, share the details below.
          </p>
          <button
            onClick={this.handleReload}
            style={{
              background: "#2f6f4f",
              color: "#fff",
              border: "none",
              padding: "10px 20px",
              borderRadius: "9999px",
              cursor: "pointer",
              fontSize: "1rem",
            }}
          >
            Reload page
          </button>
          {this.state.error && (
            <pre
              style={{
                marginTop: "1rem",
                maxWidth: "90vw",
                overflow: "auto",
                background: "#f5f5f5",
                padding: "1rem",
                borderRadius: "8px",
                fontSize: "0.75rem",
                color: "#b00020",
                textAlign: "left",
              }}
            >
              {this.state.error.message}
            </pre>
          )}
        </div>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;
